import type { HTMLAttributes } from 'react';
import { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { zIndexLayer } from '../zIndexLayer';

const ANIMATION_DURATION_MS = 300;
const CLOSING_CLASS = 'backdrop-closing';

export type BackdropProps = HTMLAttributes<HTMLDivElement> & {
  open: boolean;
  background?: string;
  fixed?: boolean;
  zIndex?: number;
  withoutTransition?: boolean;
};

export const Backdrop = forwardRef<HTMLDivElement, BackdropProps>(
  (
    {
      open,
      background = 'rgba(0, 0, 0, 0.5)',
      fixed = false,
      zIndex = zIndexLayer.modal,
      withoutTransition = false,
      className,
      ...forwardingProps
    },
    forwardingRef
  ) => {
    return (
      <BackdropContainer
        {...forwardingProps}
        _fixed={fixed}
        _zIndex={zIndex}
        _background={background}
        _withoutTransition={withoutTransition}
        className={open ? className : `${className} ${CLOSING_CLASS}`}
        ref={forwardingRef}
      />
    );
  }
);

export const BackdropContainer = styled.div<{
  _fixed: boolean;
  _zIndex: number;
  _background: string;
  _withoutTransition: boolean;
}>`
  ${({ _fixed, _zIndex, _background, _withoutTransition }) => css`
    /* Shopify conflict fix */
    display: block !important;
    position: ${_fixed ? 'fixed' : 'absolute'};
    inset: 0;
    z-index: ${_zIndex};
    background: ${_background};

    ${!_withoutTransition &&
    css`
      transition: opacity ${ANIMATION_DURATION_MS}ms ease-out;
    `}

    &.${CLOSING_CLASS} {
      opacity: 0;
    }

    @starting-style {
      & {
        opacity: 0;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      & {
        transition: none;
      }
    }
  `}
`;
